import React, { useEffect, useMemo, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useListTopicsQuery, useListLearningPathwaysQuery, useListLearningPathwaysPolyQuery } from 'src/redux/slices/api/su';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import { useAppSelector } from 'src/redux/hooks';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import LoadingView from 'src/components/LoadingView';
import ErrorView from 'src/components/ErrorView';
import { getStatusCodeFromError } from 'src/utils/error';
import { ROUTES, ROUTE_SEARCH_PARAM } from 'src/constants/route';
import { HOMEPAGE_CAROUSEL_ID } from 'src/constants/api';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import useMetrics from 'src/metrics/useUserMetrics';
import usePagePerformance from 'src/metrics/usePagePerformance';
import { FeatureName } from 'src/metrics/metricsFields';
import LearningPathwaysCarousel from 'src/components/carousel/LearningPathwaysCarousel';
import TopicsCarousel from 'src/components/carousel/TopicsCarousel';
import { useBundle } from '@amzn/react-arb-tools';
import { t } from 'src/utils/translation';
import { BUNDLE_NAME, HOMEPAGE_STRING_ID } from 'src/constants/localization';
import { TopicState } from 'src/types/state/topic';
import { LearningPathwayState } from 'src/types/state/learningPathway';
import { getAppRoute } from 'src/utils/routing';
import { LearningModuleModal } from 'src/components/learning-module';
import { EntityType } from 'src/constants/entity';
import { removeSearchParam } from 'src/utils/searchParams';
import HomepageCarouselsSection from './HomepageCarouselsSection';

/** HomePage Component */
const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { viewMetric } = useMetrics();
  const [homepageBundle] = useBundle(BUNDLE_NAME.HOMEPAGE);
  const topicsById = useAppSelector((rootState) => rootState.topics.byId);
  const learningPathwaysById = useAppSelector((rootState) => rootState.learningPathways.byId);
  const [searchParams] = useSearchParams();
  const learningModuleId = searchParams.get(ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID);
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const isPolyMigration = useAppSelector((state) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration));

  // API queries
  const {
    data: topicsData,
    isLoading: isTopicsLoading,
    isError: isTopicsError,
    error: topicsErrorObj,
  } = useListTopicsQuery({ clientId, selLocale });

  // Learning pathways API: C uses static config, T1 uses PolyD
  const {
    data: learningPathwaysPolyData,
    isLoading: isLpPolyLoading,
    isError: isLpPolyError,
    error: lpPolyErrorObj,
  } = useListLearningPathwaysPolyQuery(
    { clientId, selLocale, carouselId: HOMEPAGE_CAROUSEL_ID },
    { skip: !isPolyMigration },
  );

  // For T1: carousel title must be present in the response
  const isPolyTitleMissing = isPolyMigration && !isLpPolyLoading && !isLpPolyError
    && learningPathwaysPolyData && !learningPathwaysPolyData.title;

  const {
    data: learningPathwaysStaticData,
    isLoading: isLpStaticLoading,
    isError: isLpStaticError,
    error: lpStaticErrorObj,
  } = useListLearningPathwaysQuery(
    { clientId, selLocale },
    { skip: isPolyMigration },
  );

  // Unified LP state based on weblab treatment
  const learningPathwaysData = isPolyMigration ? learningPathwaysPolyData : learningPathwaysStaticData;
  const isLearningPathwaysLoading = isPolyMigration ? isLpPolyLoading : isLpStaticLoading;
  const isLearningPathwaysError = isPolyMigration ? isLpPolyError : isLpStaticError;
  const learningPathwaysErrorObj = isPolyMigration ? lpPolyErrorObj : lpStaticErrorObj;

  useEffect(() => {
    if (isPolyTitleMissing) {
      ClientLogger.error(ClientLog.PayloadProcessingError, {
        message: 'ListLearningPathwaysPoly response missing carousel title',
        url: window.location.href,
      });
    }
  }, [isPolyTitleMissing]);

  useEffect(() => {
    if (isPolyMigration && isLpPolyError) {
      ClientLogger.error(ClientLog.PayloadProcessingError, {
        message: 'ListLearningPathwaysPoly API request failed',
        status: (lpPolyErrorObj as Record<string, unknown>)?.status,
        url: window.location.href,
      });
    }
  }, [isPolyMigration, isLpPolyError, lpPolyErrorObj]);

  // Track page view on mount
  useEffect(() => {
    viewMetric({ feature: FeatureName.HomePage, metadata: { path: location.pathname } });
  }, []);

  // Track per-page performance (latency + availability). The page depends on both queries,
  // so it is considered loaded once neither is loading, and faulted if either errored.
  // isPolyTitleMissing is included as a fault signal so the metric matches what the user sees.
  usePagePerformance(
    FeatureName.HomePage,
    isTopicsLoading || isLearningPathwaysLoading,
    isTopicsError || isLearningPathwaysError || !!isPolyTitleMissing,
  );

  // Process topics data
  const topicCards = useMemo(() => {
    const topicIds = topicsData?.topics?.map((topic) => topic.topicId) || [];
    return topicIds
      .map((id) => topicsById[id])
      .filter(Boolean)
      .map((topic: TopicState) => ({
        id: topic.topicId,
        title: topic.title,
        description: topic.preview?.previewDescription || '',
        thumbnailLink: topic.preview?.imgSrc || '',
        buttonProps: {
          children: t(homepageBundle, HOMEPAGE_STRING_ID.LEARN_MORE_BUTTON),
          label: t(homepageBundle, HOMEPAGE_STRING_ID.LEARN_MORE_BUTTON),
          onClick: () => navigate(getAppRoute(`${ROUTES.LEARN.TOPIC}/${topic.topicId}`)),
        },
      }));
  }, [topicsData, topicsById, homepageBundle]);

  // Process learning pathways data
  const learningPathwayCards = useMemo(() => {
    const pathwayIds = learningPathwaysData?.learningPathways?.map((pathway) => pathway.learningPathwayId) || [];
    return pathwayIds
      .map((id) => learningPathwaysById[id])
      .filter(Boolean)
      .map((pathway: LearningPathwayState) => {
        const { data } = pathway;

        return {
          id: data.learningPathwayId,
          title: data.title,
          previewDescription: data.description,
          imageSrc: data.thumbnail || data.imageSrc || '',
          contentVariant: 'Video' as const,
          stats: {
            totalSections: data.learningPathwayStats?.totalSections || 0,
            totalModules: (data.learningPathwayStats?.totalDocuments || 0)
              + (data.learningPathwayStats?.totalVideos || 0),
            totalDuration: data.learningPathwayStats?.totalDuration || 0,
            totalDocuments: data.learningPathwayStats?.totalDocuments || 0,
            totalVideos: data.learningPathwayStats?.totalVideos || 0,
          },
          onClick: () => navigate(getAppRoute(`${ROUTES.LEARN.LEARNING_PATHWAY}/${data.learningPathwayId}`)),
        };
      });
  }, [learningPathwaysData, learningPathwaysById, navigate]);

  // Render section helper
  const renderSection = (isLoading: boolean, isError: boolean, errorObj: unknown, component: React.ReactNode) => {
    if (isLoading) return <LoadingView />;
    if (isError) return <ErrorView statusCode={getStatusCodeFromError(errorObj)} />;
    return component;
  };

  const homepageCarouselsComponent = useMemo(
    () => (
      <HomepageCarouselsSection
        bundleName={BUNDLE_NAME.HOMEPAGE}
        className="mb-[36px]"
      />
    ),
    [],
  );

  const removeModuleSearchParam = useCallback(() => {
    navigate({ search: removeSearchParam(searchParams, ROUTE_SEARCH_PARAM.LEARNING_MODULE_ID) }, { replace: true });
  }, [navigate, searchParams]);

  const learningModuleModalComponent = useMemo(() => {
    if (!learningModuleId) return null;
    return (
      <LearningModuleModal
        learningModuleId={learningModuleId}
        onModalClose={removeModuleSearchParam}
        parentId="homepage-modules-carousel"
        parentType={EntityType.HomePage}
      />
    );
  }, [learningModuleId, removeModuleSearchParam]);

  return (
    <div className="px-su-page-padding" data-testid="homepage">
      {homepageBundle && (
        <Helmet>
          <title>{t(homepageBundle, HOMEPAGE_STRING_ID.SEO_TITLE)}</title>
          <meta name="description" content={t(homepageBundle, HOMEPAGE_STRING_ID.SEO_DESCRIPTION)} />
          <link rel="canonical" href={`${window.location.origin}${ROUTES.LEARN.HOME}`} />
        </Helmet>
      )}
      <section className="mb-[36px]" data-testid="topics-section">
        <h2 className="mx-[28px] !mb-su-space-05 !pt-[36px] !text-su-heading-400 !font-bold">
          {t(homepageBundle, HOMEPAGE_STRING_ID.TOPICS_CAROUSEL_TITLE)}
        </h2>
        {renderSection(isTopicsLoading, isTopicsError, topicsErrorObj, <TopicsCarousel topics={topicCards} />)}
      </section>

      <section className="mb-[36px]" data-testid="pathways-section">
        {(!isPolyMigration || learningPathwaysPolyData?.title) && (
          <h2 className="mx-[28px] !mb-su-space-05 !pt-[36px] !text-su-heading-400 !font-bold">
            {isPolyMigration
              ? learningPathwaysPolyData?.title
              : t(homepageBundle, HOMEPAGE_STRING_ID.LEARNING_PATHWAY_CAROUSEL_TITLE)}
          </h2>
        )}
        {renderSection(
          isLearningPathwaysLoading,
          isLearningPathwaysError || !!isPolyTitleMissing,
          learningPathwaysErrorObj,
          <LearningPathwaysCarousel pathways={learningPathwayCards} isPolyD={isPolyMigration} />,
        )}
      </section>

      {homepageCarouselsComponent}

      {learningModuleId && learningModuleModalComponent}
    </div>
  );
};

export default HomePage;
